import * as Sentry from '@sentry/vue'
import { onErrorCaptured, ref, shallowRef } from 'vue'
import { i18n } from '@/core/config/i18n.installer'
import { useDssToast } from './messages/dss-toast.composable'

const t = (key, parameters, defaultValue) => i18n.global.t(key, parameters, defaultValue)

function getErrorMessage(error) {
  console.error(error)
  if (!error)
    return t('errors.default')

  if (error.name === 'ApiError') {
    const errorKey = `errors.api.${error.code}`
    return t(errorKey, {}, t('errors.api.default'))
  }

  if (
    error.message?.includes('Network Error')
    || error.name === 'NetworkError'
  ) {
    return t('errors.network')
  }

  if (error.message?.includes('timeout') || error.name === 'TimeoutError') {
    return t('errors.timeout')
  }

  if (error.name === 'ValidationError') {
    return t('errors.validation')
  }

  if (error.name === 'AuthenticationError') {
    return t('errors.authentication')
  }

  return (
    error.customMessage
    || error.message
    || t('errors.default')
  )
}

export function useErrorHandling() {
  const error = ref(null)
  const toast = shallowRef(null)

  function getToast() {
    if (toast.value === null) {
      try {
        toast.value = useDssToast()
      }
      catch (event) {
        console.warn(
          'Toast service not available, falling back to console',
          event,
        )

        toast.value = {
          success: (title, message) => console.warn(title, message),
          warning: (title, message) => console.warn(title, message),
          error: (title, message) => console.error(title, message),
          info: (title, message) => console.warn(title, message),
        }
      }
    }
    return toast.value
  }

  function showErrorMessage(title, message) {
    const toastService = getToast()
    toastService.error(title, message)
  }

  function handleError(error, component, info) {
    error.value = error

    const sentryContext = {
      extra: {
        component,
        info,
        errorName: error.name,
        errorCode: error.code,
        isOperational: error.isOperational || false,
      },
      tags: {
        component,
        errorType: error.name || 'Unknown',
      },
      level: error.isOperational ? 'warning' : 'error',
    }

    if (!error.isOperational || error.critical) {
      Sentry.captureException(error, sentryContext)
    }
    else {
      Sentry.captureMessage(
        `Operational error: ${error.message || 'No message'}`,
        sentryContext,
      )
    }

    const errorMessage = getErrorMessage(error)
    showErrorMessage(error.title || t('messages.error.title'), errorMessage)

    return false
  }

  function setupGlobalErrorHandlers() {
    if (typeof window !== 'undefined') {
      window.addEventListener('unhandledrejection', (event) => {
        handleError(
          event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason)),
          'UnhandledPromiseRejection',
          'Promise rejection was not caught',
        )
      })

      window.addEventListener('error', (event) => {
        if (event.error) {
          handleError(
            event.error,
            'GlobalErrorHandler',
            `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
          )
        }
      })
    }
  }

  function setupErrorCaptureHook() {
    onErrorCaptured((error_, instance, info) => {
      return handleError(
        error_,
        instance?.$options?.name || 'Unknown Component',
        info,
      )
    })
  }

  setupGlobalErrorHandlers()

  return {
    error,
    handleError,
    getErrorMessage,
    setupErrorCaptureHook,
  }
}
